<template>
  <div class="ticket-details">
    <!-- Header Compartilhado -->
    <PageHeader 
      title="Detalhes do Chamado" 
      :show-back-button="true"
      @back="voltar"
    />

    <!-- Container Principal -->
    <main class="main-container">
      <!-- Loading state -->
      <div v-if="loading" class="loading-container">
        <div class="spinner-wrapper">
          <svg class="spinner" width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="24" cy="24" r="20" stroke="#00235C" stroke-width="4" stroke-linecap="round" stroke-dasharray="125" stroke-dashoffset="62.5"/>
          </svg>
        </div>
        <p class="loading-text">Carregando detalhes...</p>
      </div>

      <!-- Card do Chamado -->
      <div v-else-if="objTarefaPrincipal != null" class="ticket-card">
        <div class="ticket-header">
          <div class="ticket-info">
            <h2 class="ticket-number">#{{ objTarefaPrincipal.codigo }}</h2>
            <span
              class="status-badge"
              :class="{
                'status-pending': objTarefaPrincipal.status === 'Pendente',
                'status-open': objTarefaPrincipal.status === 'Aberto',
                'status-closed': objTarefaPrincipal.status === 'Fechado'
              }"
            >
              {{ objTarefaPrincipal.status }}
            </span>
          </div>
          <h1 class="ticket-title-main">{{ objTarefaPrincipal.titulo }}</h1>
          <div class="ticket-metadata">
            <div class="metadata-item">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="8" cy="5" r="2.5" stroke="currentColor" stroke-width="1.5"/>
                <path d="M3 13c0-2.8 2.2-5 5-5s5 2.2 5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
              <span>{{ objTarefaPrincipal.nomeUsuario }}</span>
            </div>
            <div class="metadata-item">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13 2H3a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1zM2 6h12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>{{ formatDate(objTarefaPrincipal.data) }}</span>
            </div>
          </div>
          
          <!-- Controles Admin -->
          <div v-if="user?.isAdminDNWAYS" class="admin-controls">
            <div class="control-group">
              <label class="control-label">Status:</label>
              <select v-model="statusSelecionado" @change="atualizarChamado" class="control-select">
                <option value="Aberto">Aberto</option>
                <option value="Pendente">Pendente</option>
                <option value="Fechado">Fechado</option>
              </select>
            </div>
            <div class="control-group">
              <label class="control-label">Prioridade:</label>
              <select v-model="prioridadeSelecionada" @change="atualizarChamado" class="control-select">
                <option value="Baixa">Baixa</option>
                <option value="Media">Média</option>
                <option value="Alta">Alta</option>
                <option value="Urgente">Urgente</option>
              </select>
            </div>
          </div>
        </div>

        <!-- Timeline de Mensagens -->
        <div class="messages-timeline">
          <div
            v-for="(obj, index) in objDetalhesTarefas"
            :key="index"
            class="message-item"
            :class="{ 'admin-message': obj.isAdmin }"
          >
            <div class="message-avatar" :class="{ 'admin-avatar': obj.isAdmin }">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="8" r="4" stroke="currentColor" stroke-width="2"/>
                <path d="M4 20c0-4 3.5-7 8-7s8 3 8 7" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </div>
            <div class="message-content">
              <div class="message-header">
                <h3 class="message-title">
                  {{ obj.titulo }}
                  <span v-if="obj.isAdmin" class="admin-badge">ADMIN</span>
                </h3>
                <span class="message-date">{{ formatDate(obj.data) }}</span>
              </div>
              <p class="message-text" :class="{ 'admin-text': obj.isAdmin }">{{ obj.descricao }}</p>
              
              <!-- Anexos -->
              <div v-if="obj.anexos && obj.anexos.length > 0" class="attachments-list">
                <div class="attachments-header">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 7.5L8 13.5c-1.4 1.4-3.6 1.4-5 0s-1.4-3.6 0-5L9 2.5c.9-.9 2.4-.9 3.3 0 .9.9.9 2.4 0 3.3L6.5 11.5c-.4.4-1.1.4-1.5 0-.4-.4-.4-1.1 0-1.5L10 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <span>{{ obj.anexos.length }} arquivo(s) anexado(s)</span>
                </div>
                <div class="attachment-items">
                  <div v-for="(anexo, idx) in obj.anexos" :key="idx" class="attachment-item">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V5L9 1z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M9 1v4h4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <span class="attachment-name">{{ anexo.nomeArquivo }}</span>
                    <span class="attachment-size">({{ formatFileSize(anexo.tamanhoBytes) }})</span>
                    <button class="btn-download" @click="downloadAnexo(anexo)" title="Baixar arquivo">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M14 10v2a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-2M8 11V1M4 7l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Área de Resposta -->
        <div class="reply-section" v-if="podeResponder">
          <div class="reply-header">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 10c0 4.4-3.6 8-8 8-1.4 0-2.6-.3-3.8-1L2 18l1-4.2C2.3 12.6 2 11.4 2 10c0-4.4 3.6-8 8-8s8 3.6 8 8z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <h3>Adicionar Resposta</h3>
          </div>
          <textarea
            v-model="mensagemResposta"
            class="reply-textarea"
            placeholder="Digite sua mensagem aqui..."
            rows="4"
            :disabled="enviandoResposta"
          ></textarea>
          
          <!-- Lista de arquivos anexados -->
          <div v-if="arquivosAnexos.length > 0" class="attached-files-list">
            <div class="attached-files-header">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 7.5L8 13.5c-1.4 1.4-3.6 1.4-5 0s-1.4-3.6 0-5L9 2.5c.9-.9 2.4-.9 3.3 0 .9.9.9 2.4 0 3.3L6.5 11.5c-.4.4-1.1.4-1.5 0-.4-.4-.4-1.1 0-1.5L10 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>{{ arquivosAnexos.length }} arquivo(s) anexado(s)</span>
            </div>
            <div class="attached-files-items">
              <div v-for="(arquivo, idx) in arquivosAnexos" :key="idx" class="attached-file-item">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V5L9 1z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M9 1v4h4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span class="file-name">{{ arquivo.name }}</span>
                <span class="file-size">({{ formatFileSize(arquivo.size) }})</span>
                <button class="btn-remove-file" @click="removerArquivo(idx)" :disabled="enviandoResposta">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
          
          <input 
            ref="fileInput" 
            type="file" 
            multiple 
            style="display: none" 
            @change="selecionarArquivos"
            accept="image/*,.pdf,.doc,.docx,.txt,.zip,.rar"
          />
          
          <div class="reply-actions">
            <button class="btn-attachment" @click="$refs.fileInput.click()" :disabled="enviandoResposta">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17 9.5L10 16.5c-1.7 1.7-4.3 1.7-6 0s-1.7-4.3 0-6L11 3.5c1.1-1.1 2.9-1.1 4 0s1.1 2.9 0 4L8 14.5c-.6.6-1.4.6-2 0-.6-.6-.6-1.4 0-2L12 6.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>Anexar arquivo</span>
            </button>
            <button class="btn-send" @click="enviar" :disabled="enviandoResposta || !mensagemResposta.trim()">
              <span v-if="!enviandoResposta">Enviar</span>
              <span v-else>Enviando...</span>
              <svg v-if="!enviandoResposta" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 2L8 10M16 2l-6 14-2-6-6-2L16 2z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
        
        <!-- Mensagem de chamado fechado para clientes -->
        <div v-else-if="!user?.isAdminDNWAYS && objTarefaPrincipal?.status === 'Fechado'" class="reply-section-closed">
          <div class="closed-message">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="24" cy="24" r="20" stroke="#9ca3af" stroke-width="2" stroke-linecap="round"/>
              <path d="M24 16v8M24 28v.5" stroke="#9ca3af" stroke-width="2" stroke-linecap="round"/>
            </svg>
            <h3>Chamado Fechado</h3>
            <p>Este chamado foi fechado e não aceita mais respostas.</p>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>

import { mapGetters } from 'vuex';
import PageHeader from '@/components/PageHeader.vue';
import api from '@/api/axios';

export default {
    name: 'DashboardInicio',
    components: {
      PageHeader
    },
      computed: {
        ...mapGetters({
            user: 'auth/user',
            perfilCliente: 'GET_PERFIL_CLIENTE',
        }),
        podeResponder() {
          // Admin pode sempre responder
          if (this.user?.isAdminDNWAYS) {
            return true;
          }
          // Cliente só pode responder se chamado não estiver fechado
          return this.objTarefaPrincipal?.status !== 'Fechado';
        },
      objTarefasFiltrado(){
        if(!this.filtro && this.tab == 0)
          return this.objTarefas;

        return this.objTarefas.filter(a=> (a.titulo.toLowerCase().includes(this.filtro.toLowerCase()) || 
        a.codigo.toLowerCase().includes(this.filtro.toLowerCase())) 
        && (this.tab == 0 
        || (this.tab == 1 && a.status == 'Aberto') 
        || (this.tab == 2 && a.status == 'Fechado') 
        || (this.tab == 3 && a.status == 'Pendente')))
      }
    },
    data() {
        return {
            filtro: '',
            guidChamado: '',
            mensagemResposta: '',
            dadosUsuario: {
              PerfilRisco: ''
            },
            loginSession: {
                Usuario: null,
                Senha: null,
            },
            objTarefaPrincipal: null,
            objDetalhesTarefas: [],
            tab: 0,
            loading: false,
            error: null,
            arquivosAnexos: [],
            enviandoResposta: false,
            statusSelecionado: '',
            prioridadeSelecionada: '',
        };
    },
    mounted(){
      this.guidChamado = this.$route.params.id;
      this.$store.commit("SET_ABRIR_MODAL_CHAMADO", false);
      this.dadosUsuario.nome = localStorage.getItem('nomeRazaoSocial');
      this.nomeEmpresa = process.env.VUE_APP_NOME_EMPRESA;
      
      // Carrega os detalhes do chamado
      this.carregarDetalhes();
    },
    methods: {
      formatDate(date) {
        return date.toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }).replace(' de ', ' ');
      },
      async enviar() {
        if (!this.mensagemResposta.trim()) {
          this.$swal({
            icon: 'warning',
            title: 'Atenção',
            text: 'Por favor, digite uma mensagem antes de enviar.',
          });
          return;
        }

        this.enviandoResposta = true;

        try {
          // Cria FormData para enviar arquivos
          const formData = new FormData();
          formData.append('ChamadoId', this.guidChamado);
          formData.append('Mensagem', this.mensagemResposta);
          // Admin envia respostas internas (Interno = true), clientes enviam públicas (Interno = false)
          formData.append('Interno', this.user?.isAdminDNWAYS ? 'true' : 'false');

          // Adiciona arquivos anexados
          this.arquivosAnexos.forEach((arquivo) => {
            formData.append('Anexos', arquivo);
          });

          await api.post('/resposta', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            },
            withCredentials: true
          });

          this.$swal({
            icon: 'success',
            title: 'Sucesso!',
            text: 'Resposta enviada com sucesso.',
            timer: 2000,
            showConfirmButton: false
          });

          // Limpa o formulário
          this.mensagemResposta = '';
          this.arquivosAnexos = [];
          if (this.$refs.fileInput) {
            this.$refs.fileInput.value = '';
          }

          // Recarrega os detalhes para mostrar a nova resposta
          await this.carregarDetalhes();

          this.enviandoResposta = false;
        } catch (error) {
          this.enviandoResposta = false;
          console.error('Erro ao enviar resposta:', error);

          if (error.response?.status === 401) {
            this.$swal({
              icon: 'error',
              title: 'Sessão expirada',
              text: 'Por favor, faça login novamente.',
            }).then(() => {
              this.$router.push('/login');
            });
          } else if (error.response?.status === 400) {
            this.$swal({
              icon: 'error',
              title: 'Erro de validação',
              text: error.response?.data?.message || error.response?.data?.Message || 'Dados inválidos.',
            });
          } else {
            this.$swal({
              icon: 'error',
              title: 'Erro ao enviar resposta',
              text: error.response?.data?.message || error.response?.data?.Message || 'Ocorreu um erro ao enviar a resposta.',
            });
          }
        }
      },
      selecionarArquivos(event) {
        const files = Array.from(event.target.files);
        const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
        
        for (const file of files) {
          if (file.size > MAX_FILE_SIZE) {
            this.$swal({
              icon: 'warning',
              title: 'Arquivo muito grande',
              text: `O arquivo "${file.name}" excede o tamanho máximo de 10MB.`,
            });
            continue;
          }
          
          // Verifica se o arquivo já foi adicionado
          const jaExiste = this.arquivosAnexos.some(a => 
            a.name === file.name && a.size === file.size
          );
          
          if (!jaExiste) {
            this.arquivosAnexos.push(file);
          }
        }
      },
      removerArquivo(index) {
        this.arquivosAnexos.splice(index, 1);
        // Limpa o input file para permitir re-selecionar o mesmo arquivo
        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = '';
        }
      },
      async downloadAnexo(anexo) {
        try {
          // Define o endpoint correto baseado no tipo de anexo
          const endpoint = anexo.tipo === 'chamado' 
            ? `/anexo/chamado/${anexo.id}`
            : `/anexo/resposta/${anexo.id}`;
          
          // Faz a requisição para baixar o arquivo
          const response = await api.get(endpoint, {
            responseType: 'blob', // Importante para receber o arquivo como blob
            withCredentials: true
          });

          // Cria um URL temporário para o blob
          const url = window.URL.createObjectURL(new Blob([response.data]));
          
          // Cria um link temporário e clica nele para iniciar o download
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', anexo.nomeArquivo);
          document.body.appendChild(link);
          link.click();
          
          // Limpa o link e o URL temporário
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } catch (error) {
          console.error('Erro ao baixar anexo:', error);
          
          if (error.response?.status === 401) {
            this.$swal({
              icon: 'error',
              title: 'Sessão expirada',
              text: 'Por favor, faça login novamente.',
            }).then(() => {
              this.$router.push('/login');
            });
          } else if (error.response?.status === 404) {
            this.$swal({
              icon: 'error',
              title: 'Arquivo não encontrado',
              text: 'O arquivo solicitado não foi encontrado.',
            });
          } else {
            this.$swal({
              icon: 'error',
              title: 'Erro ao baixar arquivo',
              text: 'Ocorreu um erro ao tentar baixar o arquivo. Tente novamente.',
            });
          }
        }
      },
      voltar(){
          this.$router.push('/');
      },
      abrirChamado(){
        console.log('bate aqui');
        this.$store.commit("SET_ABRIR_MODAL_CHAMADO", true);
      },
      selectTab(tabSelected){
          this.tab = tabSelected;
        },
      async carregarDetalhes() {
        this.loading = true;
        this.error = null;
        
        try {
          const response = await api.get(`/chamado/${this.guidChamado}`, {
            withCredentials: true
          });
          
          const data = response.data;
          
          // Mapeia dados do chamado principal
          this.objTarefaPrincipal = {
            guid: data.id || data.Id,
            codigo: (data.id || data.Id).toString(),
            titulo: data.titulo || data.Titulo,
            descricao: data.descricao || data.Descricao,
            status: data.status || data.Status,
            data: new Date(data.dataAbertura || data.DataAbertura),
            dataFechamento: data.dataFechamento || data.DataFechamento,
            prioridade: data.prioridade || data.Prioridade,
            nomeUsuario: data.nomeUsuario || data.NomeUsuario,
            emailUsuario: data.emailUsuario || data.EmailUsuario,
            anexos: this.mapearAnexos(data.anexos || data.Anexos || [], 'chamado')
          };
          
          // Inicializa os selects do admin com os valores atuais
          this.statusSelecionado = this.objTarefaPrincipal.status;
          this.prioridadeSelecionada = this.objTarefaPrincipal.prioridade;
          
          // Monta timeline com mensagem inicial + respostas
          this.objDetalhesTarefas = [];
          
          // Adiciona mensagem inicial do chamado
          this.objDetalhesTarefas.push({
            guid: data.id || data.Id,
            data: new Date(data.dataAbertura || data.DataAbertura),
            titulo: `${data.nomeUsuario || data.NomeUsuario} abriu o chamado`,
            descricao: data.descricao || data.Descricao,
            nomeUsuario: data.nomeUsuario || data.NomeUsuario,
            anexos: this.mapearAnexos(data.anexos || data.Anexos || [], 'chamado'),
            isAdmin: false // Mensagem inicial não é do admin
          });
          
          // Adiciona respostas
          const respostas = data.respostas || data.Respostas || [];
          respostas.forEach(resposta => {
            // Verifica se a resposta é de um admin (campo Interno = true ou emailUsuario contém dnways)
            const isAdmin = (resposta.interno || resposta.Interno) || 
                           (resposta.emailUsuario || resposta.EmailUsuario || '').toLowerCase().includes('dnways');
            
            this.objDetalhesTarefas.push({
              guid: resposta.id || resposta.Id,
              data: new Date(resposta.dataResposta || resposta.DataResposta),
              titulo: resposta.nomeUsuario || resposta.NomeUsuario,
              descricao: resposta.mensagem || resposta.Mensagem,
              nomeUsuario: resposta.nomeUsuario || resposta.NomeUsuario,
              interno: resposta.interno || resposta.Interno,
              anexos: this.mapearAnexos(resposta.anexos || resposta.Anexos || [], 'resposta'),
              isAdmin: isAdmin
            });
          });
          
          this.loading = false;
        } catch (error) {
          this.loading = false;
          console.error('Erro ao carregar detalhes:', error);
          
          if (error.response?.status === 404) {
            this.$swal({
              icon: 'error',
              title: 'Chamado não encontrado',
              text: 'O chamado solicitado não existe ou foi removido.',
            }).then(() => {
              this.$router.push('/');
            });
          } else if (error.response?.status === 401) {
            this.$swal({
              icon: 'error',
              title: 'Sessão expirada',
              text: 'Por favor, faça login novamente.',
            }).then(() => {
              this.$router.push('/login');
            });
          } else {
            this.error = 'Erro ao carregar detalhes do chamado.';
            this.$swal({
              icon: 'error',
              title: 'Erro ao carregar detalhes',
              text: error.response?.data?.message || error.response?.data?.Message || 'Ocorreu um erro ao buscar os detalhes do chamado.',
            });
          }
        }
      },
      mapearAnexos(anexos, tipo) {
        return anexos.map(anexo => ({
          id: anexo.id || anexo.Id,
          nomeArquivo: anexo.nomeArquivo || anexo.NomeArquivo,
          tamanhoBytes: anexo.tamanhoBytes || anexo.TamanhoBytes,
          tipoConteudo: anexo.tipoConteudo || anexo.TipoConteudo,
          dataUpload: new Date(anexo.dataUpload || anexo.DataUpload),
          tipo: tipo // 'chamado' ou 'resposta'
        }));
      },
      formatFileSize(bytes) {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
      },
      async atualizarChamado() {
        if (!this.user?.isAdminDNWAYS) {
          this.$swal({
            icon: 'error',
            title: 'Sem permissão',
            text: 'Apenas administradores podem atualizar chamados.',
          });
          return;
        }
        
        try {
          const request = {
            Id: parseInt(this.guidChamado),
            Status: this.statusSelecionado,
            Prioridade: this.prioridadeSelecionada
          };
          
          await api.put(`/chamado/${this.guidChamado}`, request, {
            withCredentials: true
          });
          
          // Atualiza o objeto local
          this.objTarefaPrincipal.status = this.statusSelecionado;
          this.objTarefaPrincipal.prioridade = this.prioridadeSelecionada;
          
          this.$swal({
            icon: 'success',
            title: 'Atualizado!',
            text: 'Chamado atualizado com sucesso.',
            timer: 2000,
            showConfirmButton: false
          });
        } catch (error) {
          console.error('Erro ao atualizar chamado:', error);
          
          if (error.response?.status === 401) {
            this.$swal({
              icon: 'error',
              title: 'Sessão expirada',
              text: 'Por favor, faça login novamente.',
            }).then(() => {
              this.$router.push('/login');
            });
          } else if (error.response?.status === 403) {
            this.$swal({
              icon: 'error',
              title: 'Sem permissão',
              text: 'Você não tem permissão para atualizar este chamado.',
            });
          } else {
            this.$swal({
              icon: 'error',
              title: 'Erro ao atualizar',
              text: error.response?.data?.message || error.response?.data?.Message || 'Ocorreu um erro ao atualizar o chamado.',
            });
          }
        }
      },
    }
};

</script>

<style lang="scss" scoped>

@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

/* Reset e Variáveis */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.ticket-details {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  font-family: 'Inter', sans-serif;
}

/* Container Principal */
.main-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
}

/* Loading State */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
}

.spinner-wrapper {
  margin-bottom: 1.5rem;
}

.spinner {
  animation: rotate 1s linear infinite;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  font-size: 1rem;
  color: #6b7280;
  font-weight: 500;
}

/* Card do Chamado */
.ticket-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.ticket-header {
  padding: 1.5rem 2rem;
  border-bottom: 2px solid #f3f4f6;
  background: linear-gradient(to bottom, #ffffff 0%, #f9fafb 100%);
}

.ticket-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.ticket-number {
  font-size: 1.75rem;
  font-weight: 700;
  color: #00235C;
  letter-spacing: -0.5px;
}

.status-badge {
  padding: 0.5rem 1rem;
  border-radius: 24px;
  font-size: 0.8125rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &.status-open {
    background: #FEF3C7;
    color: #D97706;
  }

  &.status-closed {
    background: #D1FAE5;
    color: #059669;
  }

  &.status-pending {
    background: #DBEAFE;
    color: #2563EB;
  }
}

.ticket-title-main {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin-top: 1rem;
  line-height: 1.3;
}

.ticket-metadata {
  display: flex;
  gap: 1.5rem;
  margin-top: 0.75rem;
  flex-wrap: wrap;
}

.metadata-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;

  svg {
    color: #9ca3af;
  }
}

/* Controles Admin */
.admin-controls {
  display: flex;
  gap: 1.5rem;
  margin-top: 1.5rem;
  padding: 1.25rem;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-radius: 12px;
  border: 2px solid #bae6fd;
  flex-wrap: wrap;
}

.control-group {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.control-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #0369a1;
  white-space: nowrap;
}

.control-select {
  padding: 0.5rem 1rem;
  background: white;
  border: 2px solid #bae6fd;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #0c4a6e;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #0ea5e9;
  }

  &:focus {
    outline: none;
    border-color: #0284c7;
    box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
  }
}

/* Timeline de Mensagens */
.messages-timeline {
  padding: 2rem;
  max-height: 500px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f3f4f6;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 4px;

    &:hover {
      background: #9ca3af;
    }
  }
}

.message-item {
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  animation: fadeIn 0.3s ease;

  &:last-child {
    margin-bottom: 0;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.message-avatar {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #00235C 0%, #001a45 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;

  svg {
    width: 24px;
    height: 24px;
  }
  
  &.admin-avatar {
    background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
    box-shadow: 0 4px 12px rgba(14, 165, 233, 0.4);
  }
}

.message-content {
  flex: 1;
  min-width: 0;
}

.message-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 1rem;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
}

.message-title {
  font-size: 1rem;
  font-weight: 700;
  color: #00235C;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.admin-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.625rem;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  color: white;
  font-size: 0.625rem;
  font-weight: 800;
  letter-spacing: 0.5px;
  border-radius: 12px;
  text-transform: uppercase;
  box-shadow: 0 2px 8px rgba(14, 165, 233, 0.3);
}

.message-date {
  font-size: 0.8125rem;
  color: #9ca3af;
  font-weight: 500;
}

.message-text {
  font-size: 0.9375rem;
  color: #4b5563;
  line-height: 1.6;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 12px;
  border-left: 3px solid #00235C;
  
  &.admin-text {
    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
    border-left-color: #0ea5e9;
    color: #0c4a6e;
    font-weight: 500;
  }
}

/* Anexos */
.attachments-list {
  margin-top: 1rem;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.attachments-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #6b7280;

  svg {
    color: #00235C;
  }
}

.attachment-items {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.attachment-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.8125rem;
  transition: all 0.2s ease;

  &:hover {
    background: #f3f4f6;
    border-color: #d1d5db;
  }

  > svg {
    color: #6b7280;
    flex-shrink: 0;
  }
}

.attachment-name {
  flex: 1;
  color: #374151;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.attachment-size {
  color: #9ca3af;
  font-size: 0.75rem;
  flex-shrink: 0;
  margin-right: 0.5rem;
}

.btn-download {
  padding: 0.375rem;
  background: transparent;
  border: 1px solid #e5e7eb;
  color: #00235C;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    background: #00235C;
    border-color: #00235C;
    color: white;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 35, 92, 0.2);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 16px;
    height: 16px;
  }
}

/* Seção de Resposta */
.reply-section {
  padding: 2rem;
  border-top: 2px solid #f3f4f6;
  background: #fafbfc;
}

/* Mensagem de chamado fechado */
.reply-section-closed {
  padding: 3rem 2rem;
  border-top: 2px solid #f3f4f6;
  background: #fafbfc;
}

.closed-message {
  max-width: 400px;
  margin: 0 auto;
  text-align: center;
  padding: 2rem;
  background: white;
  border-radius: 16px;
  border: 2px solid #e5e7eb;

  svg {
    margin: 0 auto 1.5rem;
  }

  h3 {
    font-size: 1.25rem;
    font-weight: 700;
    color: #6b7280;
    margin-bottom: 0.5rem;
  }

  p {
    font-size: 0.9375rem;
    color: #9ca3af;
    line-height: 1.6;
  }
}

.reply-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;

  svg {
    color: #00235C;
  }

  h3 {
    font-size: 1.125rem;
    font-weight: 700;
    color: #1f2937;
  }
}

.reply-textarea {
  width: 100%;
  padding: 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-family: 'Inter', sans-serif;
  font-size: 0.9375rem;
  line-height: 1.6;
  color: #1f2937;
  resize: vertical;
  min-height: 120px;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #00235C;
    box-shadow: 0 0 0 3px rgba(0, 35, 92, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }

  &:disabled {
    background-color: #f9fafb;
    cursor: not-allowed;
    opacity: 0.6;
  }
}

/* Lista de arquivos anexados (para envio) */
.attached-files-list {
  margin-top: 1rem;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.attached-files-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #6b7280;

  svg {
    color: #00235C;
  }
}

.attached-files-items {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.attached-file-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.8125rem;
  transition: all 0.2s ease;

  svg {
    color: #6b7280;
    flex-shrink: 0;
  }
}

.file-name {
  flex: 1;
  color: #374151;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.file-size {
  color: #9ca3af;
  font-size: 0.75rem;
  flex-shrink: 0;
}

.btn-remove-file {
  padding: 0.25rem;
  background: transparent;
  border: none;
  color: #ef4444;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: #fee2e2;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 14px;
    height: 14px;
  }
}

.reply-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
  flex-wrap: wrap;
}

.btn-attachment {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: white;
  color: #6b7280;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    background: #f9fafb;
    border-color: #00235C;
    color: #00235C;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 20px;
    height: 20px;
  }
}

.btn-send {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 2rem;
  background: linear-gradient(135deg, #00235C 0%, #001a45 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(0, 35, 92, 0.3);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: #9ca3af;
  }

  svg {
    width: 18px;
    height: 18px;
  }
}

/* Responsividade */
@media (max-width: 1024px) {
  .main-container {
    padding: 1.5rem;
  }

  .header-title {
    font-size: 1.5rem;
  }

  .messages-timeline {
    max-height: 400px;
  }
}

@media (max-width: 768px) {
    .header-content {
      flex-wrap: wrap;
      padding: 1.25rem 1.5rem;
    }

    .header-title {
      font-size: 1.375rem;
      order: 1;
      flex-basis: 100%;
      text-align: left;
    }

    .btn-back {
      order: 2;
    }

    .btn-logoff {
      order: 3;
      margin-left: auto;
    }

    .main-container {
      padding: 1.25rem;
    }

    .ticket-header {
      padding: 1.25rem 1.5rem;
    }

    .ticket-number {
      font-size: 1.5rem;
    }

    .messages-timeline {
      padding: 1.5rem;
      max-height: 350px;
    }

    .message-item {
      gap: 0.75rem;
    }

    .message-avatar {
      width: 36px;
      height: 36px;

      svg {
        width: 20px;
        height: 20px;
      }
    }

    .reply-section {
      padding: 1.5rem;
    }

    .reply-actions {
      flex-direction: column;
      align-items: stretch;
    }

    .btn-attachment,
    .btn-send {
      width: 100%;
      justify-content: center;
    }
  }

  @media (max-width: 480px) {
    .header-content {
      padding: 1rem;
    }

    .header-title {
      font-size: 1.25rem;
    }

    .btn-back,
    .btn-logoff {
      padding: 0.5rem 1rem;
      font-size: 0.8125rem;

      span {
        display: none;
      }
    }

    .main-container {
      padding: 1rem;
    }

    .ticket-header {
      padding: 1rem;
    }

    .ticket-number {
      font-size: 1.25rem;
    }

    .status-badge {
      font-size: 0.75rem;
      padding: 0.375rem 0.75rem;
    }

    .messages-timeline {
      padding: 1rem;
      max-height: 300px;
    }

    .message-item {
      flex-direction: column;
      align-items: flex-start;
    }

    .message-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.25rem;
    }

    .message-title {
      font-size: 0.9375rem;
    }

    .message-text {
      font-size: 0.875rem;
      padding: 0.875rem;
    }

    .reply-section {
      padding: 1rem;
    }

    .reply-header h3 {
      font-size: 1rem;
    }

    .reply-textarea {
      font-size: 0.875rem;
      min-height: 100px;
    }

    .btn-attachment,
    .btn-send {
      font-size: 0.8125rem;
      padding: 0.625rem 1rem;
    }
  }
</style>